import { Sidebar } from "./Sidebar";
import { MobileBottomNav } from "./MobileBottomNav";
import { Paywall } from "@/components/Paywall";
import { trpc } from "@/lib/trpc";
import { useCommunity } from "@/contexts/CommunityContext";
import { useEffect } from "react";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { data: user, isLoading } = trpc.user.me.useQuery();
  const { selectedCommunityId, setSelectedCommunityId } = useCommunity();
  const { data: communities } = trpc.amenity.getUserCommunities.useQuery(
    undefined,
    {
      enabled: !!user,
    },
  );

  useEffect(() => {
    if (!communities) return;

    if (communities.length === 0) {
      if (selectedCommunityId !== null) {
        setSelectedCommunityId(null);
      }
      return;
    }

    const hasValidSelection = selectedCommunityId
      ? communities.some((community) => community.id === selectedCommunityId)
      : false;

    if (!hasValidSelection) {
      setSelectedCommunityId(communities[0].id);
    }
  }, [communities, selectedCommunityId, setSelectedCommunityId]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Show paywall if user doesn't belong to any community (unless they're a super admin)
  if (!user?.hasCommunity && !user?.isSuperAdmin) {
    return <Paywall />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Main content - add bottom padding on mobile for nav bar */}
      <main className="flex-1 overflow-auto pb-24 md:pb-0">{children}</main>

      {/* Mobile bottom navigation - hidden on desktop */}
      <MobileBottomNav />
    </div>
  );
}
